// PairScreen — the only thing the operator sees when the kiosk is
// unpaired. Six-character code → POST /devices/claim → on success the
// parent App swaps in the kiosk surface.

import { type FormEvent, useState } from "react";

import {
  Button,
  Card,
  PageShell,
  Stack,
  TextField,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import {
  type Client,
  type DevicePairResponse,
  isApiError,
} from "@sms/sdk";

import { makeT } from "./i18n";

export interface PairScreenProps {
  locale: Locale;
  client: Pick<Client, "claimDevicePair">;
  onPaired: (resp: DevicePairResponse) => void;
}

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string };

export function PairScreen({ locale, client, onPaired }: PairScreenProps) {
  const t = makeT(locale);
  const [code, setCode] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) {
      setState({ kind: "error", message: t("gate.pair_failed") });
      return;
    }
    setState({ kind: "submitting" });
    try {
      const resp = await client.claimDevicePair(trimmed);
      onPaired(resp);
    } catch (err) {
      const reason =
        isApiError(err) && err.problem.detail
          ? err.problem.detail
          : t("gate.pair_failed");
      setState({ kind: "error", message: reason });
    }
  };

  return (
    <PageShell
      brand={<GateGlyph />}
      title={t("gate.pair_title")}
      tagline={t("gate.pair_help")}
    >
      <Card variant="elevated" padding={5}>
        <form onSubmit={submit}>
          <Stack gap={4}>
            <TextField
              label={t("gate.pair_code")}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
              minLength={4}
              maxLength={10}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              variant="code"
              error={
                state.kind === "error" ? state.message : undefined
              }
            />
            <Button
              type="submit"
              size="lg"
              fullWidth
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t("common.loading")
                : t("gate.pair_submit")}
            </Button>
          </Stack>
        </form>
      </Card>
    </PageShell>
  );
}

function GateGlyph() {
  // Generic "kiosk on a gate" mark. Same brand fill as the shell's
  // open-book glyph so the visual family reads as one product.
  return (
    <div style={brandStyle} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="28"
        height="28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 20h8" />
        <path d="M12 18v2" />
        <path d="M7 9h.01M11 9h.01M15 9h.01" />
        <path d="M7 13h10" />
      </svg>
    </div>
  );
}

const brandStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 16,
  background: tokens.color.brand[500],
  color: tokens.color.neutral[0],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "inset 0 0 0 1.5px rgba(255, 255, 255, 0.14)",
};
